import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fetchProduct, fetchSettings, uploadUrl } from "@/lib/api";
import { fmtCAD } from "@/lib/currency";
import { variantName, variantPrice, variantSku, variantQty, priceRange } from "@/lib/variants";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { testIds } from "@/constants/testIds/b2b";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1622290291468-a28f7a7dc6a8?auto=format&fit=crop&w=1200&q=80";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [error, setError] = useState(false);
  const [qtyBySize, setQtyBySize] = useState({}); // { size: number }
  const [prebookLabel, setPrebookLabel] = useState("Prebook · SS26");

  const { user, isApproved } = useAuth();
  const { addItems, openDrawer } = useCart();

  useEffect(() => {
    (async () => {
      try {
        const p = await fetchProduct(id);
        setProduct(p);
      } catch (e) {
        setError(true);
      }
    })();
  }, [id]);

  useEffect(() => {
    fetchSettings().then((s) => { if (s?.prebook_label) setPrebookLabel(s.prebook_label); }).catch(() => {});
  }, []);

  const totalUnits = useMemo(
    () => Object.values(qtyBySize).reduce((sum, v) => sum + (parseInt(v, 10) || 0), 0),
    [qtyBySize]
  );
  const total = useMemo(() => {
    if (!product) return 0;
    return (product.sizes || []).reduce((sum, s) => {
      const n = parseInt(qtyBySize[variantName(s)], 10) || 0;
      return sum + n * variantPrice(s, product);
    }, 0);
  }, [qtyBySize, product]);

  if (error) {
    return (
      <div data-testid="product-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold mb-3">Product not found</h1>
        <Link to="/shop" className="text-primary underline">Back to products</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div data-testid="product-loading" className="max-w-6xl mx-auto px-4 md:px-6 py-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="aspect-[4/5] rounded-md bg-secondary animate-pulse" />
        <div className="space-y-3">
          <div className="h-8 bg-secondary rounded animate-pulse" />
          <div className="h-5 bg-secondary rounded w-1/3 animate-pulse" />
          <div className="h-24 bg-secondary rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const images = product.images?.length ? product.images : [FALLBACK_IMG];
  const showWholesale = product.wholesale_price != null;

  const handleAdd = () => {
    const pairs = (product.sizes || [])
      .map((s) => {
        const name = variantName(s);
        return {
          size: name,
          quantity: parseInt(qtyBySize[name], 10) || 0,
          price: variantPrice(s, product),
          sku: variantSku(s, product),
        };
      })
      .filter((p) => p.quantity > 0);
    if (pairs.length === 0) {
      toast.error("Enter a quantity for at least one size.");
      return;
    }
    const result = addItems(product, pairs);
    if (result && result.ok === false && result.reason === "mixed") {
      const itemKind = (product?.product_type || product?.fulfillment_type) === "in_stock" ? "in-stock" : "prebook";
      const cartKind = itemKind === "in-stock" ? "prebook" : "in-stock";
      toast.error(
        `You can't mix ${itemKind} items with ${cartKind} items. Place your ${cartKind} order first, or clear the cart to start a new ${itemKind} order.`,
        { duration: 6000 }
      );
      return;
    }
    setQtyBySize({});
    toast.success(`Added ${totalUnits} units to the order pad.`);
    openDrawer();
  };

  return (
    <div data-testid="product-detail-page" className="page-card">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <Link to="/shop" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-shop">
          <ArrowLeft size={13} strokeWidth={1.5} />
          Back to products
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12">
          {/* Gallery */}
          <div className="md:col-span-3">
            <div className="aspect-square overflow-hidden rounded-md bg-secondary border border-border">
              <img src={uploadUrl(images[activeImg])} alt={product.name} className="w-full h-full object-contain" data-testid="product-main-image" />
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-thin">
                {images.map((src, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImg(idx)}
                    data-testid={`thumb-${idx}`}
                    className={`w-16 h-20 shrink-0 rounded overflow-hidden border-2 transition-colors ${activeImg === idx ? "border-primary" : "border-transparent"}`}
                  >
                    <img src={uploadUrl(src)} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Order pad */}
          <div className="md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{product.category}</p>
            <h1 className="text-2xl md:text-3xl font-semibold leading-tight" data-testid="product-name">{product.name}</h1>

            <div className="mt-4 flex flex-col gap-1">
              {showWholesale ? (
                <>
                  <div className="flex items-baseline gap-2 text-sm tabular-nums">
                    <span className="uppercase tracking-[0.12em] text-muted-foreground text-xs">MSRP</span>
                    <span className="text-foreground/80" data-testid={testIds.productMsrp}>{fmtCAD(product.msrp)}</span>
                  </div>
                  <div className="flex items-baseline gap-2 tabular-nums">
                    <span className="uppercase tracking-[0.12em] text-muted-foreground text-xs">Wholesale</span>
                    <span className="text-2xl font-semibold" data-testid={testIds.productWholesale}>
                      {(() => { const r = priceRange(product); return r.hasRange ? `${fmtCAD(r.min)}–${fmtCAD(r.max)}` : fmtCAD(product.wholesale_price); })()}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-2xl font-semibold tabular-nums" data-testid={testIds.productMsrp}>
                  {fmtCAD(product.msrp)} <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground ml-1">MSRP</span>
                </p>
              )}
            </div>

            <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-destructive tabular-nums" data-testid={testIds.productMoq}>
              {product.product_type === "in_stock"
                ? "In stock · ships immediately"
                : product.delivery_window
                  ? `Estimated ship window · ${product.delivery_window}`
                  : prebookLabel}
            </p>
            {product.order_by_date && (
              <p className="mt-0.5 text-[11px] uppercase tracking-[0.12em] text-destructive tabular-nums">
                Order by {product.order_by_date}
              </p>
            )}

            {product.description && (
              <p className="mt-5 text-sm text-foreground/80 leading-relaxed" data-testid="product-description">{product.description}</p>
            )}

            {!showWholesale ? (
              <div data-testid={testIds.productGatedNotice} className="mt-6 p-4 rounded-md border border-border bg-secondary text-sm">
                <p className="flex items-center gap-2 font-medium mb-1">
                  <Lock size={13} strokeWidth={1.5} /> Wholesale pricing locked
                </p>
                <p className="text-muted-foreground">
                  {user
                    ? "Your account is pending approval. Wholesale pricing and ordering will unlock once approved."
                    : <><Link to="/register" className="underline">Apply</Link> or <Link to="/login" className="underline">sign in</Link> with an approved account to view wholesale.</>}
                </p>
              </div>
            ) : (
              <div className="mt-6 border border-border rounded-md overflow-hidden">
                <div className="px-3 py-2 bg-secondary border-b border-border flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Order by size</p>
                </div>
                <div className="divide-y divide-border">
                  {(product.sizes || []).map((s) => {
                    const name = variantName(s);
                    const unit = variantPrice(s, product);
                    const v = qtyBySize[name] ?? "";
                    const n = parseInt(v, 10) || 0;
                    const line = n * unit;
                    const sku = typeof s === "object" ? s.sku : "";
                    const isStock = product.product_type === "in_stock";
                    const avail = variantQty(s);
                    const tracked = isStock && avail != null;
                    const soldOut = tracked && avail <= 0;
                    return (
                      <div key={name} className={`flex items-center gap-3 px-3 py-2 hover:bg-secondary/40 ${soldOut ? "opacity-60" : ""}`}>
                        <span className="text-sm w-16 shrink-0">
                          {name}
                          {sku ? <span className="block text-[10px] text-muted-foreground font-mono truncate">{sku}</span> : null}
                        </span>
                        <input
                          data-testid={`${testIds.productSize}-${name}`}
                          type="number"
                          min={0}
                          max={tracked ? avail : undefined}
                          step={1}
                          placeholder={soldOut ? "0" : "0"}
                          disabled={soldOut}
                          value={v}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (tracked && val !== "" && parseInt(val, 10) > avail) val = String(avail);
                            setQtyBySize({ ...qtyBySize, [name]: val });
                          }}
                          className="w-20 px-2 py-1.5 text-sm text-center border rounded tabular-nums focus:border-primary border-border disabled:bg-secondary disabled:cursor-not-allowed"
                        />
                        <span className="text-[11px] text-muted-foreground tabular-nums w-16 text-right shrink-0">{fmtCAD(unit)}</span>
                        {tracked ? (
                          <span
                            data-testid={`product-stock-${name}`}
                            className={`text-[10px] uppercase tracking-[0.1em] w-20 text-right shrink-0 ${soldOut ? "text-destructive" : "text-muted-foreground"}`}
                          >
                            {soldOut ? "Out of stock" : `${avail} left`}
                          </span>
                        ) : (
                          <span className="ml-auto text-sm tabular-nums text-muted-foreground w-20 text-right">
                            {n > 0 ? fmtCAD(line) : "—"}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="px-3 py-3 bg-secondary border-t border-border flex items-center justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Total</p>
                    <p className="text-base font-semibold tabular-nums">
                      {totalUnits} units · {fmtCAD(total)}
                    </p>
                  </div>
                  <button
                    onClick={handleAdd}
                    data-testid={testIds.productAddToCart}
                    disabled={!isApproved || totalUnits === 0}
                    className="px-4 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 inline-flex items-center gap-1.5"
                  >
                    <Check size={14} strokeWidth={2} /> Add to order
                  </button>
                </div>
              </div>
            )}

            {isApproved && (
              <p className="mt-4 text-[11px] text-muted-foreground">
                Invoice issued on confirmation · no card required
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
